/**
 * Retry utility with exponential backoff for API calls
 *
 * This utility provides robust retry logic for folder upload operations,
 * handling network failures, temporary server errors, and other transient issues.
 */

export interface RetryOptions {
  /** Maximum number of retry attempts (default: 3) */
  maxRetries?: number
  /** Initial delay between retries in milliseconds (default: 1000) */
  initialDelay?: number
  /** Maximum delay between retries in milliseconds (default: 10000) */
  maxDelay?: number
  /** Multiplier for exponential backoff (default: 2) */
  backoffMultiplier?: number
  /** Jitter factor to randomize delays (default: 0.1) */
  jitter?: number
  /** Function to determine if an error should trigger a retry */
  shouldRetry?: (error: any) => boolean
  /** Function called before each retry attempt */
  onRetry?: (attempt: number, error: any) => void | Promise<void>
}

export interface RetryResult<T> {
  /** The successful result */
  result: T
  /** Number of attempts made (including the successful one) */
  attempts: number
  /** Total time spent on retries in milliseconds */
  totalTime: number
}

/**
 * Default retry configuration for folder upload operations
 */
export const DEFAULT_RETRY_OPTIONS: Required<RetryOptions> = {
  maxRetries: 3,
  initialDelay: 1000,
  maxDelay: 10000,
  backoffMultiplier: 2,
  jitter: 0.1,
  shouldRetry: (error: any) => {
    // Retry on network errors, 5xx server errors, and specific 4xx errors
    if (error?.code === 'NETWORK_ERROR' || error?.message?.includes('Network Error')) {
      return true
    }

    if (error?.response?.status >= 500) {
      return true
    }

    // Retry on specific 4xx errors that might be temporary
    if (error?.response?.status === 429) {
      // Don't retry storage quota exceeded errors
      const errorCode = error?.response?.data?.error?.code || error?.response?.data?.code

      if (errorCode === 'USER_STORAGE_QUOTA_EXCEEDED') {
        return false
      }

      // Rate limiting - retry other 429 errors
      return true
    }

    if (error?.response?.status === 408) {
      // Request timeout
      return true
    }

    if (error?.response?.status === 409) {
      // Conflict (might be temporary)
      return true
    }

    // Retry on S3-specific errors
    if (
      error?.message?.includes('RequestTimeout') ||
      error?.message?.includes('ServiceUnavailable') ||
      error?.message?.includes('InternalError') ||
      error?.message?.includes('SlowDown')
    ) {
      return true
    }

    return false
  },
  onRetry: (attempt: number, error: any) => {
    console.warn(`🔄 [Retry] Attempt ${attempt} failed, retrying...`, {
      error: error?.message || error,
      status: error?.response?.status,
      timestamp: new Date().toISOString(),
    })
  },
}

/**
 * Calculate delay with exponential backoff and jitter
 */
function calculateDelay(attempt: number, options: Required<RetryOptions>): number {
  const exponentialDelay = options.initialDelay * Math.pow(options.backoffMultiplier, attempt - 1)
  const cappedDelay = Math.min(exponentialDelay, options.maxDelay)

  // Add jitter to prevent thundering herd
  const jitterAmount = cappedDelay * options.jitter * Math.random()
  const finalDelay = cappedDelay + jitterAmount

  return Math.round(finalDelay)
}

/**
 * Sleep for the specified number of milliseconds
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

/**
 * Execute a function with retry logic and exponential backoff
 *
 * @param fn Function to execute
 * @param options Retry configuration
 * @returns Promise that resolves to the result or rejects after all retries fail
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {},
): Promise<RetryResult<T>> {
  const config = { ...DEFAULT_RETRY_OPTIONS, ...options }
  const startTime = Date.now()
  let lastError: any

  for (let attempt = 1; attempt <= config.maxRetries + 1; attempt++) {
    try {
      const result = await fn()
      const totalTime = Date.now() - startTime

      if (attempt > 1) {
        console.log(`✅ [Retry] Success after ${attempt} attempts in ${totalTime}ms`)
      }

      return {
        result,
        attempts: attempt,
        totalTime,
      }
    } catch (error) {
      lastError = error

      // Don't retry on the last attempt
      if (attempt > config.maxRetries) {
        break
      }

      // Check if we should retry this error
      if (!config.shouldRetry(error)) {
        break
      }

      // Call onRetry callback
      await config.onRetry(attempt, error)

      // Calculate delay and wait
      const delay = calculateDelay(attempt, config)

      console.log(`⏳ [Retry] Waiting ${delay}ms before attempt ${attempt + 1}...`)
      await sleep(delay)
    }
  }

  const totalTime = Date.now() - startTime

  console.error(
    `❌ [Retry] Failed after ${config.maxRetries + 1} attempts in ${totalTime}ms:`,
    lastError,
  )

  throw lastError
}

/**
 * Specialized retry function for folder upload API calls
 *
 * @param apiCall Function that makes the API call
 * @param context Context information for logging
 * @returns Promise that resolves to the API response
 */
export async function retryFolderUploadApi<T>(
  apiCall: () => Promise<T>,
  context: { operation: string; folderId?: string; fileName?: string },
): Promise<T> {
  const { result } = await withRetry(apiCall, {
    maxRetries: 3,
    initialDelay: 1000,
    maxDelay: 8000,
    shouldRetry: (error: any) => {
      // For bulkCompleteUploads, handle 404 errors specially
      if (context.operation === 'bulkCompleteUploads' && error?.response?.status === 404) {
        console.warn(
          `🚫 [FolderUpload] 404 error detected for bulkCompleteUploads, checking deleted files...`,
          {
            folderId: context.folderId,
            fileName: context.fileName,
            error: error?.message || error,
            timestamp: new Date().toISOString(),
          },
        )

        // Don't retry 404 errors - they indicate files were deleted
        return false
      }

      // Don't retry storage quota exceeded errors (429 with USER_STORAGE_QUOTA_EXCEEDED code)
      if (error?.response?.status === 429) {
        const errorCode = error?.response?.data?.error?.code || error?.response?.data?.code

        if (errorCode === 'USER_STORAGE_QUOTA_EXCEEDED') {
          console.warn(`🚫 [FolderUpload] Storage quota exceeded, not retrying:`, {
            folderId: context.folderId,
            fileName: context.fileName,
            error: error?.message || error,
            timestamp: new Date().toISOString(),
          })

          return false
        }
      }

      // Use default retry logic for other operations and errors
      return DEFAULT_RETRY_OPTIONS.shouldRetry(error)
    },
    onRetry: async (attempt, error) => {
      console.warn(`🔄 [FolderUpload] ${context.operation} failed, retrying (${attempt}/3):`, {
        folderId: context.folderId,
        fileName: context.fileName,
        error: error?.message || error,
        status: error?.response?.status,
        timestamp: new Date().toISOString(),
      })

      // Show user-friendly retry notification for critical operations
      if (context.operation === 'initiateFolderUpload') {
        const { toast } = await import('sonner')

        toast.warning(`Retrying folder upload initiation... (attempt ${attempt}/3)`)
      }
    },
  })

  return result
}

/**
 * Specialized retry function for S3 upload operations
 *
 * @param uploadCall Function that performs the S3 upload
 * @param context Context information for logging
 * @returns Promise that resolves to the upload result
 */
export async function retryS3Upload<T>(
  uploadCall: () => Promise<T>,
  context: { fileName: string; chunkIndex?: number; totalChunks?: number },
): Promise<T> {
  const { result } = await withRetry(uploadCall, {
    maxRetries: 2, // Fewer retries for S3 uploads to avoid long delays
    initialDelay: 500,
    maxDelay: 5000,
    onRetry: (attempt, error) => {
      const chunkInfo =
        context.chunkIndex !== undefined
          ? ` (chunk ${context.chunkIndex + 1}/${context.totalChunks})`
          : ''

      console.warn(
        `🔄 [S3Upload] ${context.fileName}${chunkInfo} failed, retrying (${attempt}/2):`,
        {
          error: error?.message || error,
          status: error?.response?.status,
          timestamp: new Date().toISOString(),
        },
      )
    },
  })

  return result
}
